import { differenceInCalendarDays, format } from "date-fns";

import type { SponsoringStage } from "../types";

export const findStageLabel = (stages: SponsoringStage[], stageValue: string) =>
  stages.find((stage) => stage.value === stageValue)?.label;

/** A sponsoring "ends soon" when its end date is within this many days */
export const ENDS_SOON_DAYS = 90;

const isoDateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

/** Format a Date as an ISO date string (YYYY-MM-DD) in the local timezone */
export const toISODate = (date: Date) => format(date, "yyyy-MM-dd");

/**
 * Parse an ISO date string (YYYY-MM-DD) as a local date, or null when
 * invalid. Parsing the components by hand avoids the off-by-one-day issue
 * of `new Date("YYYY-MM-DD")`, which some browsers treat as UTC.
 */
export const parseISODate = (dateString?: string | null): Date | null => {
  if (!dateString || !isoDateStringRegex.test(dateString)) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

export type EndDateStatus =
  | { kind: "expired"; days: number }
  | { kind: "today"; days: 0 }
  | { kind: "soon"; days: number }
  | null;

/**
 * Describe how an end date relates to today: expired (in the past), ending
 * today, ending soon (within ENDS_SOON_DAYS), or null when nothing is worth
 * flagging.
 */
export const getEndDateStatus = (
  endDate?: string | null,
  today: Date = new Date(),
): EndDateStatus => {
  const end = parseISODate(endDate);
  if (!end) return null;
  const days = differenceInCalendarDays(end, today);
  if (days < 0) return { kind: "expired", days };
  if (days === 0) return { kind: "today", days: 0 };
  if (days <= ENDS_SOON_DAYS) return { kind: "soon", days };
  return null;
};

export type StageTone = "funnel" | "active" | "closed";

export const getStageTone = (
  stage: string,
  {
    sponsoringActiveStatuses,
    sponsoringClosedStatuses,
  }: { sponsoringActiveStatuses: string[]; sponsoringClosedStatuses: string[] },
): StageTone => {
  if (sponsoringActiveStatuses.includes(stage)) return "active";
  if (sponsoringClosedStatuses.includes(stage)) return "closed";
  return "funnel";
};
